/**
 * Asset History & Reports — the org-wide "History & Reports" admin screen over
 * `asset.contract.ts`'s `AssetHistory` events.
 *
 * `asset.contract.ts`'s `getAssetHistory` (`GET /assets/:assetId/history`) is single-asset only —
 * built for the asset detail page and the current custodian's own view, and gated accordingly
 * (custodian-or-`asset:read`, checked against that one asset). Asset > Administration's "History &
 * Reports" tab needs the cross-asset equivalent: every lifecycle event across the register,
 * filterable by asset/event type/person/location/date range, paginated, plus a CSV export of the
 * same rows — so it gets its own router here, split out the same way
 * asset-configuration.contract.ts/vendor.contract.ts/asset-batch.contract.ts already split off
 * Administration's other tabs rather than growing `asset.contract.ts` further (see
 * asset-batch.contract.ts's header for the same reasoning).
 *
 * Reuses `AssetHistory`'s existing shape (`assetHistoryEventTypeSchema`, `assetConditionSchema`,
 * `custodyTypeSchema`, `assetSummaryPersonSchema`) from `asset.contract.ts` instead of redefining
 * it, and adds only what a cross-asset listing needs on top of the single-asset `AssetHistoryEvent`:
 * which asset and location each row belongs to.
 *
 * Both routes declare `asset:read` — the same permission `getAsset`/App.tsx's
 * `/asset-management/administration/history` route already gate on — checked here with
 * `canAnywhere` as the coarse "may this actor reach the feature at all" pre-filter; the actual
 * per-row visibility is `buildAssetScopeWhere` (asset.ts), the same grants-based scoping
 * `listAssets`/`listAllocations` already use, applied to each event's own asset. The export adds
 * `asset:export` on top, same AND-of-two-permissions shape as
 * vendor.contract.ts's/asset-batch.contract.ts's own CSV exports.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import {
  assetConditionSchema,
  assetHistoryEventTypeSchema,
  assetSummaryPersonSchema,
  custodyTypeSchema,
  listAssetsQuerySchema,
} from './asset.contract.js';
import { assetCsvExportSchema } from './vendor.contract.js';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/**
 * One row of the org-wide listing — `AssetHistoryEvent` (asset.contract.ts) plus the asset/location
 * identity a cross-asset table needs and a single-asset page already has from context.
 */
export const assetHistoryListItemSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  assetId: uuidSchema,
  assetCode: z.string(),
  assetName: z.string(),
  locationId: uuidSchema.nullable(),
  locationName: z.string().nullable(),
  eventType: assetHistoryEventTypeSchema,
  custodyType: custodyTypeSchema.nullable().optional(),
  actorPersonId: uuidSchema.nullable(),
  actor: assetSummaryPersonSchema.nullable().optional(),
  fromPersonId: uuidSchema.nullable().optional(),
  fromPerson: assetSummaryPersonSchema.nullable().optional(),
  toPersonId: uuidSchema.nullable().optional(),
  toPerson: assetSummaryPersonSchema.nullable().optional(),
  conditionBefore: assetConditionSchema.nullable().optional(),
  conditionAfter: assetConditionSchema.nullable().optional(),
  assetRequestId: uuidSchema.nullable().optional(),
  note: z.string().nullable().optional(),
  createdAt: instantSchema,
});
export type AssetHistoryListItem = z.infer<typeof assetHistoryListItemSchema>;

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

export const listAssetHistoryQuerySchema = paginationQuerySchema.extend({
  assetId: uuidSchema.optional(),
  eventType: assetHistoryEventTypeSchema.optional(),
  /** Matches the event's actor OR either side of a custody change (from/to). */
  personId: uuidSchema.optional(),
  /** The asset's own location at read time, not the acting person's. */
  locationId: uuidSchema.optional(),
  dateFrom: dateOnlySchema.optional(),
  dateTo: dateOnlySchema.optional(),
});
export type ListAssetHistoryQuery = z.infer<typeof listAssetHistoryQuerySchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const assetHistoryContract = c.router(
  {
    listAssetHistory: {
      method: 'GET',
      path: '/',
      query: listAssetHistoryQuerySchema,
      summary:
        'List asset lifecycle history events org-wide, filtered and paginated (asset:read, row-scoped)',
      metadata: routeMeta('asset:read'),
      responses: {
        200: paginatedSchema(assetHistoryListItemSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },
    // Declared after listAssetHistory (its path is a subpath, not `/:id`, so ordering has no
    // routing effect here — kept next to it for readability, same as the batches/vendors exports).
    exportAssetHistory: {
      method: 'GET',
      path: '/export',
      query: listAssetHistoryQuerySchema.omit({ cursor: true, limit: true }),
      summary: 'Export the same filtered asset history rows as CSV (asset:export + asset:read)',
      metadata: routeMeta(['asset:export', 'asset:read']),
      responses: {
        200: assetCsvExportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },
    // The asset REGISTER export (not history) — same router because it shares the export
    // permission shape and pathPrefix's reporting area; path is distinct from '/export' above.
    exportAssetRegister: {
      method: 'GET',
      path: '/register/export',
      query: listAssetsQuerySchema.omit({ cursor: true, limit: true }),
      summary:
        'Export every in-scope asset matching the register filters as CSV (asset:export + asset:read)',
      metadata: routeMeta(['asset:export', 'asset:read']),
      responses: {
        200: assetCsvExportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },
  },
  { pathPrefix: '/asset-management/history' },
);
